import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import logger from "../../shared/utils/logger";
import { trackEvent } from "../../shared/services/playerEvents";
import { Button } from "@/components/ui/button";

/**
 * DobGate: blocking 18+ attestation sheet for social-login users.
 *
 * Google OAuth never returns a date of birth, so the backend age gate refuses
 * to stamp geo_verified=true and the account is locked out of every player
 * endpoint. AuthContext flips `needsDob` on when the registration trigger comes
 * back with errorCode "DOB_REQUIRED"; this non-dismissable sheet then presents
 * a ONE-TAP affirmative 18+ attestation and re-fires the trigger so the account
 * is unblocked.
 *
 * Compliance note: the affirmative tap on the clearly-labelled 18+ button IS
 * the age consent (hard age is still verified by ID at KYC before any
 * redemption). The affirmation wording next to the button is therefore
 * load-bearing and must stay present and legible.
 *
 * Intentionally NOT dismissable (no close button, no backdrop/Esc dismissal):
 * the account is unusable until the attestation is on file.
 */
const DobGate = () => {
  const { needsDob, completeRegistrationAttestation, signOut } = useAuth();

  const [error, setError] = useState("");
  const [terminal, setTerminal] = useState(null); // { title, message } when account can't proceed
  const [submitting, setSubmitting] = useState(false);

  // player_events funnel: the OAuth age wall is the key drop-off we're
  // diagnosing. Emit `dob_gate_shown` once each time the gate opens (reset when
  // it closes so a later re-open re-fires). Fire-and-forget.
  const shownRef = useRef(false);
  useEffect(() => {
    if (needsDob && !shownRef.current) {
      shownRef.current = true;
      trackEvent("dob_gate_shown");
    } else if (!needsDob) {
      shownRef.current = false;
    }
  }, [needsDob]);

  if (!needsDob) return null;

  const handleSignOut = async () => {
    // player_events funnel: leaving the age gate without completing it. Fire
    // first (fire-and-forget) so the beacon has a head start before the
    // sign-out + redirect below navigate the tab away.
    trackEvent("dob_gate_abandoned");
    try {
      await signOut();
    } catch (e) {
      logger.error("DobGate sign-out failed", e);
    } finally {
      window.location.assign("/signin");
    }
  };

  const handleAttest = async () => {
    if (submitting) return;
    setError("");

    setSubmitting(true);
    const result = await completeRegistrationAttestation();
    setSubmitting(false);

    if (result.ok) {
      // player_events funnel: age wall cleared. Fire first (fire-and-forget)
      // so the beacon starts before the redirect below.
      trackEvent("dob_gate_completed");
      // geo_verified is now stamped; reload so every previously-403'd query
      // refetches against an unblocked account.
      window.location.assign("/");
      return;
    }

    // Terminal outcomes: the account cannot proceed from this device.
    if (result.errorCode === "UNDER_AGE") {
      setTerminal({
        title: "You must be 18 or older",
        message:
          "Cluck.us is only available to players aged 18 and over. This account cannot be activated.",
      });
      return;
    }
    if (result.errorCode === "GEO_BLOCKED") {
      setTerminal({
        title: "Not available in your location",
        // Prefer the server's specific wording (lists eligible regions); fall
        // back to a clear US/CA-only message. Covers e.g. a VPN-off UK IP.
        message:
          result.message ||
          "Cluck.us is only available to residents of the United States and Canada. We couldn't verify your current location as eligible. If you're using a VPN, turn it off and try again.",
      });
      return;
    }
    if (result.errorCode === "NO_SESSION") {
      setTerminal({
        title: "Session expired",
        message: "Please sign in again to finish setting up your account.",
      });
      return;
    }

    // Recoverable error: let them retry.
    setError(result.message || "Something went wrong. Please try again.");
  };

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dob-gate-title"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-card-foreground shadow-lg sm:p-8">
        {terminal ? (
          <div className="space-y-4 text-center">
            <h2 id="dob-gate-title" className="text-lg font-semibold">
              {terminal.title}
            </h2>
            <p className="text-sm text-muted-foreground">{terminal.message}</p>
            <Button type="button" className="w-full" onClick={handleSignOut}>
              Sign out
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-2 text-center sm:text-left">
              <h2 id="dob-gate-title" className="text-xl font-semibold">
                One last step
              </h2>
              <p className="text-sm text-muted-foreground">
                Confirm you're 18 or older to start playing on Cluck.us.
              </p>
            </div>

            {error ? (
              <p className="text-sm text-red-500" role="alert">
                {error}
              </p>
            ) : null}

            <div className="space-y-3">
              <Button
                type="button"
                variant="gold"
                size="lg"
                className="w-full"
                onClick={handleAttest}
                disabled={submitting}
                aria-describedby="dob-gate-affirmation"
              >
                {submitting ? "Entering…" : "I'm 18+ · Enter Cluck.us"}
              </Button>

              <p
                id="dob-gate-affirmation"
                className="text-xs leading-relaxed text-muted-foreground"
              >
                By tapping above, you confirm you are 18 or older and agree to
                our{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline underline-offset-2"
                >
                  Official Rules &amp; Terms
                </a>{" "}
                and{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline underline-offset-2"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>

            <button
              type="button"
              onClick={handleSignOut}
              className="w-full text-center text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DobGate;
